/**
 * rrweb recorder lifecycle.
 *
 * Owns exactly one recording at a time and knows nothing about WHY it is
 * recording — that decision belongs to the server (see /v1/replay/policy) and is
 * plumbed through by ./index.js. Keeping the two apart is what makes it possible
 * to test the enable rules without a browser and the buffering without a policy.
 */

import { buildRecordOptions } from './masking';
import { createCssTable, dedupeEventCss } from './cssDedupe';
import { uploadChunk } from './uploader';
import { cacheChunk, clearCachedChunk } from './chunkCache';

const DEFAULT_CHUNK_MS = 30_000;
const MAX_EVENTS_PER_CHUNK = 2000;

/**
 * Flush early once a chunk reaches this much serialised JSON.
 *
 * The event count alone bounds nothing: one `cpt-emr` batch is a single event
 * carrying hundreds of EMR events, so the largest chunk measured in the bucket
 * was 31 MB holding six events, and only the 30-second timer ever ended it.
 *
 * 8 MB, not tighter: the server caps a replay at MAX_CHUNKS_PER_REPLAY = 240,
 * which means "two hours" only if every chunk covers 30 seconds. Every early
 * flush spends one of those chunks on less than 30 seconds of session, so a
 * tight gate quietly shortens the recording — replaying a real two-hour capture
 * through these rules, a 2 MB gate cut it off at 94 minutes; 8 MB cost 3 chunks.
 */
const MAX_CHUNK_BYTES = 8 * 1024 * 1024;

/**
 * Hard ceiling on buffered bytes. Past this the recorder STOPS.
 *
 * The flush above is not a memory guarantee: flush() serialises on the upload
 * already in flight, so while a chunk is stuck the gate keeps firing and the
 * buffer keeps growing behind it. This is the line that actually holds.
 *
 * It stops rather than trims, and the difference matters. Every event here is
 * load-bearing — mutations apply positionally against the mirror, so a hole
 * corrupts everything after it — and the panel stream's Meta and FullSnapshot
 * are the ONLY ones it ever emits (no checkoutEveryNms, see masking.js). Being
 * first, they are also the first thing any drop-oldest rule takes: an earlier
 * version trimmed, the EMR pump's first drain (one cpt-emr event carrying a
 * 25 MB opening snapshot) evicted them, and the replay came back with a black
 * panel pane. A truncated replay is watchable; a snapshot-less one is not.
 *
 * Sized well above the largest single event we legitimately produce, so a big
 * opening EMR snapshot flushes as its own chunk instead of ending the recording.
 * (The EMR-side buffer in EmrReplayRecorder.ts DOES drop oldest — it heals with
 * a fresh snapshot on the next drain. Nothing here heals.)
 */
const MAX_BUFFER_BYTES = 48 * 1024 * 1024;

class Recorder {
  constructor() {
    this.reset();
  }

  reset() {
    this.apiClient = null;
    this.replayId = null;
    this.startedAt = null;
    this.stopFn = null;
    // Serialised events, not live ones — see onEvent().
    this.buffer = [];
    this.bufferBytes = 0;
    this.stopping = false;
    this.cssTable = null;
    // Chunks the upload lost. The EMR pump reads this: the EMR batches in a lost
    // chunk are gone and everything after them is incremental against a state
    // the viewer will never see, so it asks the page for a fresh snapshot.
    this.lostChunks = 0;
    this.sequence = 0;
    this.eventCount = 0;
    this.bytesStored = 0;
    this.errorCount = 0;
    this.timer = null;
    this.maxChunks = Infinity;
    this.pending = null;
    this.addCustomEvent = null;
  }

  /**
   * Emit a sidecar record onto the replay timeline as an rrweb custom event.
   *
   * Custom events ride the existing chunk pipe (no backend change) and land in
   * timeline order, which is what lets the viewer line a network call or an
   * injector message up against the frame where it happened.
   *
   * Whatever is passed here is stored verbatim. Callers are responsible for
   * gating — see sidecar.js, where every stream that can carry chart content is
   * off unless the server turned it on.
   */
  addCustom(tag, payload) {
    if (!this.isRecording || this.stopping || !this.addCustomEvent) return false;
    try {
      this.addCustomEvent(tag, payload);
      return true;
    } catch (e) {
      // A sidecar must never take the replay (or the app) down with it.
      console.log('replay: custom event dropped', tag, e);
      return false;
    }
  }

  get isRecording() {
    return this.stopFn !== null;
  }

  /**
   * Begin recording. Resolves false (never throws) if rrweb can't load or
   * recording can't start — the app must boot normally either way.
   */
  async start(apiClient, policy) {
    if (this.isRecording) return true;
    // Two callers can arrive while rrweb is still loading — the boot policy and
    // a bug report. The second waits for the first rather than starting a
    // second rrweb instance into the same buffer.
    if (this.starting) return this.starting;
    this.starting = this.startNow(apiClient, policy).finally(() => {
      this.starting = null;
    });
    return this.starting;
  }

  async startNow(apiClient, policy) {
    let record;
    try {
      // Dynamic import so rrweb lands in its own chunk: the ~140 KB library is
      // dead weight for the majority of loads that are not being recorded, and
      // this is the app clinicians wait on between patients.
      // addCustomEvent rides along for the sidecar streams (see sidecar.js).
      ({ record, addCustomEvent: this.addCustomEvent } = await import('rrweb'));
    } catch (e) {
      console.log('replay: rrweb failed to load, not recording', e);
      return false;
    }

    this.apiClient = apiClient;
    this.replayId = policy.replayId;
    this.maxChunks = policy.maxChunks || Infinity;
    this.startedAt = Date.now();
    // Per replay, and cleared by reset(): the ids in it are only meaningful
    // within the stream of chunks they were written into.
    this.cssTable = createCssTable();

    try {
      this.stopFn = record({
        ...buildRecordOptions(),
        emit: (event) => this.onEvent(event),
      });
    } catch (e) {
      console.log('replay: rrweb record() failed', e);
      this.reset();
      return false;
    }

    const chunkMs = (policy.chunkSeconds || DEFAULT_CHUNK_MS / 1000) * 1000;
    this.timer = setInterval(() => this.flush(), chunkMs);

    // A closed tab is the common ending, not the exception — most replays never
    // reach an explicit stop(). visibilitychange fires in cases pagehide does
    // not (tab discard, mobile background), so both are wired, and both are
    // held on `this` so stop() can actually remove them: an on-demand replay
    // can be started and stopped repeatedly in one page life.
    //
    // keepalive is what makes these flushes actually arrive: without it the
    // browser cancels in-flight fetches as the page goes away, so the final
    // chunk of every tab-close replay — the end of the session, i.e. the part
    // worth watching — is lost. Browsers cap a keepalive body at 64 KB, which
    // is the real bound on MAX_EVENTS_PER_CHUNK below.
    this.onHide = () => this.flush({ keepalive: true });
    this.onVisibility = () => {
      if (document.visibilityState === 'hidden') this.flush({ keepalive: true });
    };
    window.addEventListener('pagehide', this.onHide);
    document.addEventListener('visibilitychange', this.onVisibility);

    return true;
  }

  /**
   * Buffer one event as JSON text rather than as the live object.
   *
   * A parsed object graph costs several times what its JSON does, and the
   * buffer is what sits in the clinician's tab between flushes; serialising
   * here spreads the work over one event at a time instead of a multi-megabyte
   * stringify on the main thread every 30 s; and it gives an exact byte count,
   * which is what the two bounds below need and an event count never could.
   */
  onEvent(event) {
    let text;
    try {
      // De-duplicate first: on an EMR page most of these bytes are repeated
      // stylesheets, so this is what makes the serialise cheap.
      text = JSON.stringify(dedupeEventCss(event, this.cssTable));
    } catch (e) {
      // Fails closed, like masking.js: an event we cannot serialise now would
      // only fail the whole chunk at flush time, taking its neighbours with it.
      console.log('replay: event dropped, could not be serialised', e);
      return;
    }
    if (text === undefined) return;

    this.buffer.push(text);
    this.bufferBytes += text.length;
    this.eventCount += 1;

    // A burst (a big DOM swap, a long note being typed) can outrun the timer.
    if (this.buffer.length >= MAX_EVENTS_PER_CHUNK || this.bufferBytes >= MAX_CHUNK_BYTES) {
      this.flush();
    }

    // Uploads are not draining the buffer. Stop, never trim — see MAX_BUFFER_BYTES.
    if (this.bufferBytes > MAX_BUFFER_BYTES && !this.stopping) {
      this.stopping = true;
      console.log('replay: uploads cannot keep up, stopping recorder to bound memory');
      // Deferred: we are inside rrweb's emit callback and stopWithoutUpload()
      // calls rrweb's own stop().
      setTimeout(() => this.stopWithoutUpload(), 0);
    }
  }

  /** Note that an error happened during this replay, so the viewer can sort by it. */
  noteError() {
    this.errorCount += 1;
  }

  async flush({ keepalive = false } = {}) {
    if (!this.isRecording) return;
    // Serialise on the in-flight upload instead of dropping this call. Returning
    // early here is what made stop()'s final flush a coin toss: if the periodic
    // timer happened to be mid-upload, the last buffered events were silently
    // discarded and reset() then ran underneath the upload.
    if (this.pending) await this.pending.catch(() => {});
    if (!this.isRecording || this.buffer.length === 0) return;

    if (this.sequence >= this.maxChunks) {
      // Hit the server's cap. Drop the buffer and tear down WITHOUT calling
      // stop(): stop() flushes, and flush() used to call stop() here, so the two
      // recursed into each other until the stack blew.
      console.log('replay: chunk limit reached, stopping recorder');
      this.buffer = [];
      this.bufferBytes = 0;
      this.stopWithoutUpload();
      return;
    }

    // Already JSON text (see onEvent), so the chunk body is a join rather than
    // a second full serialise of everything.
    const body = `[${this.buffer.join(',')}]`;
    this.buffer = [];
    this.bufferBytes = 0;
    const sequence = this.sequence;
    this.sequence += 1;

    // `keepalive` means the page may be going away, and that is the flush that
    // usually does not arrive — the browser rejects a keepalive body over 64 KB
    // and cancels an ordinary one with the page. Leave it where the next boot
    // can find it; the upload below still gets its chance first.
    if (keepalive) cacheChunk(this.apiClient, this.replayId, sequence, body);

    // try/finally, not a bare assignment: if this ever throws, a stuck `pending`
    // would block every later flush and let the buffer grow without bound —
    // precisely on the user whose network is the bug being investigated.
    this.pending = (async () => {
      try {
        // On failure the events are gone on purpose. Re-queueing them would grow
        // the buffer without bound on a user whose network is the actual bug.
        const stored = await uploadChunk(
          this.apiClient,
          this.replayId,
          sequence,
          body,
          { keepalive },
        );
        this.bytesStored += stored;
        if (!stored) this.lostChunks += 1;
        // Arrived after all — usually a tab switch rather than a close, since an
        // upload rarely outlives the page. Nothing left to carry over.
        if (stored && keepalive) clearCachedChunk(this.replayId, sequence);
      } finally {
        this.pending = null;
      }
    })();

    await this.pending;
  }

  /**
   * Stop immediately, dropping anything still buffered. For logout, where the
   * session credentials are being torn down in the same tick and any upload we
   * started would fail — see discardSessionReplay(). Synchronous on purpose:
   * the guarantee that matters is that nothing recorded after this point can be
   * attributed to the user who just logged out.
   */
  stopWithoutUpload() {
    if (!this.isRecording) return;
    try {
      this.stopFn();
    } catch (e) {
      console.log('replay: stop() threw', e);
    }
    clearInterval(this.timer);
    window.removeEventListener('pagehide', this.onHide);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.reset();
  }

  /** Stop recording and report totals. Safe to call when not recording. */
  async stop() {
    if (!this.isRecording) return null;
    this.stopping = true;

    try {
      this.stopFn();
    } catch (e) {
      console.log('replay: stop() threw', e);
    }
    clearInterval(this.timer);
    window.removeEventListener('pagehide', this.onHide);
    document.removeEventListener('visibilitychange', this.onVisibility);

    // Keep stopFn set through the final flush so it isn't skipped as "not recording".
    await this.flush();

    const summary = {
      replayId: this.replayId,
      chunkCount: this.sequence,
      eventCount: this.eventCount,
      bytesStored: this.bytesStored,
      errorCount: this.errorCount,
    };
    this.reset();
    return summary;
  }
}

// One recorder per tab. A module-level singleton rather than React state: the
// recorder has to outlive every route change and re-render in the app.
const recorder = new Recorder();
export default recorder;
